import { z } from "zod";
import { groupPurchaseSchema, groupSelectionAvailable, ticketSlotSchema } from "./group-checkout.ts";

// Bump for incompatible draft changes. Older namespaces are ignored, not migrated.
export const CHECKOUT_STORAGE_VERSION = 1;
const checkoutStoragePrefix = `checkout:v${CHECKOUT_STORAGE_VERSION}`;

export const checkoutSteps = ["tickets", "guests", "details", "phone", "profile", "questions", "email", "payment"] as const;
export type CheckoutStage = (typeof checkoutSteps)[number] | "status";

const purchaseAttemptSchema = z.object({
    expectedTotalCents: z.number().int().nonnegative().max(2147483647),
    ticketVariationID: z.string().max(100),
    discountCode: z.string().max(100),
    purchaserDetails: z.string().max(1000000),
    group: groupPurchaseSchema.optional(),
    purchaserUserID: z.string().max(100).optional(),
    approvalRequired: z.boolean().optional(),
  });

export const checkoutDraftSchema = z.object({
  step: z.enum(checkoutSteps).default("tickets"),
  ticketVariationID: z.string().max(100).default(""),
  groupSlots: z.array(ticketSlotSchema).default([]),
  purchaserSlotID: z.string().max(100).default(""),
  registrationID: z.string().max(100).default(""),
  discountCode: z.string().max(100).default(""),
  phone: z.string().max(40).default(""),
  phoneInput: z.string().max(60).default(""),
  phoneCountry: z.string().max(2).default(""),
  verificationSentAt: z.number().int().nonnegative().default(0),
  contactEmail: z.string().max(254).default(""),
  profile: z.object({
    name: z.string().max(100).default(""),
    age: z.string().max(3).default(""),
    gender: z.string().max(100).default(""),
    headline: z.string().max(500).default(""),
    profileImageUrl: z.string().max(2048).default(""),
    stage: z.string().max(30).default(""),
  }).default({ name: "", age: "", gender: "", headline: "", profileImageUrl: "", stage: "" }),
  answers: z.record(z.string(), z.object({
    text: z.string().max(5000).default(""),
    selected: z.array(z.string().max(1000)).max(100).default([]),
  })).refine(answers => Object.keys(answers).length <= 100).default({}),
  message: z.string().max(500).default(""),
  walletIntentID: z.string().max(100).default(""),
  // Frozen command terms let an ambiguous response retry the same purchase.
  attempt: purchaseAttemptSchema.nullable().default(null),
  previousAttempt: z.object({
    command: purchaseAttemptSchema.nullable(),
    walletIntentID: z.string().max(100),
    registrationID: z.string().max(100),
  }).nullable().default(null),
  editingPayment: z.boolean().default(false),
  paymentPhase: z.enum(["overview", "method", "processing"]).default("overview"),
});
export type CheckoutDraft = z.infer<typeof checkoutDraftSchema>;

// The retry key identifies the accepted command, not a change to its contents.
export function sameCheckoutPurchase(left: CheckoutDraft["attempt"], right: CheckoutDraft["attempt"]): boolean {
  if (!left || !right) return false;
  const terms = (attempt: NonNullable<CheckoutDraft["attempt"]>) => [
    attempt.expectedTotalCents, attempt.ticketVariationID, attempt.discountCode,
    attempt.purchaserDetails, attempt.purchaserUserID, attempt.approvalRequired,
    attempt.group?.quoteFingerprint, attempt.group?.additionalTickets,
  ];
  return JSON.stringify(terms(left)) === JSON.stringify(terms(right));
}

export const emptyCheckoutDraft = (): CheckoutDraft => checkoutDraftSchema.parse({});

// An unsubmitted saved bag is only useful while its tickets can still be bought.
// Accepted or uncertain purchases must keep their original recovery identity.
export function availableCheckoutDraft(
  draft: CheckoutDraft,
  plan: Parameters<typeof groupSelectionAvailable>[1],
): CheckoutDraft {
  if (draft.attempt || draft.walletIntentID || draft.previousAttempt) return draft;
  const valid = draft.groupSlots.length
    ? groupSelectionAvailable(draft.groupSlots, plan)
    : !draft.ticketVariationID || plan.ticketVariations.some(variation =>
      variation.id === draft.ticketVariationID && variation.available);
  return valid ? draft : emptyCheckoutDraft();
}

export function checkoutRecoveryDraft(walletIntentID: string): CheckoutDraft {
  return { ...emptyCheckoutDraft(), walletIntentID, paymentPhase: "processing" };
}

export function checkoutStorageKey(eventId: string, userId?: string): string {
  return `${checkoutStoragePrefix}:${userId ? `user:${userId}` : "visitor"}:${eventId}`;
}

export function activateCheckoutIdentity(storage: Storage, userId?: string): void {
  const identity = userId ? `user:${userId}` : "visitor";
  const identityKey = `${checkoutStoragePrefix}:identity`;
    const previous = storage.getItem(identityKey);
  if (previous && previous !== identity) {
    const prefix = `${checkoutStoragePrefix}:${previous}:`;
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) {
        let previousDraft: CheckoutDraft | null;
        try { previousDraft = readCheckoutDraft(storage, key); }
        catch (error) { if (error instanceof CheckoutRecoveryUnavailableError) continue; throw error; }
        if (previousDraft?.attempt || previousDraft?.walletIntentID || previousDraft?.previousAttempt) {
          // Retain recovery only in its original account namespace. Strip editable
          // personal fields without losing the accepted command needed to replay.
          writeCheckoutDraft(storage, key, { ...checkoutRecoveryDraft(previousDraft.walletIntentID), attempt: previousDraft.attempt, previousAttempt: previousDraft.previousAttempt, registrationID: previousDraft.registrationID });
        } else storage.removeItem(key);
      }
    }
  }
  storage.setItem(identityKey, identity);
}

export class CheckoutRecoveryUnavailableError extends Error {}

export function readCheckoutDraft(storage: Storage, key: string, now = Date.now()): CheckoutDraft | null {
  const value = storage.getItem(key);
  if (!value) return null;
  let hasFinancialRecovery = false;
  try {
    const stored = JSON.parse(value);
    hasFinancialRecovery = !!stored.draft?.attempt || !!stored.draft?.walletIntentID || !!stored.recovery?.attempt || !!stored.recovery?.walletIntentID || !!stored.recovery?.previousAttempt;
    if (stored.recovery) stored.draft = { ...stored.draft, ...stored.recovery };
    // Older tabs saved status screens as checkout progress. Keep only their
    // recovery handle until the API verifies the outcome, never their old form.
    if (stored.draft?.step === "status") {
      const { walletIntentID } = checkoutDraftSchema.parse({ walletIntentID: stored.draft.walletIntentID });
      stored.draft = checkoutRecoveryDraft(walletIntentID);
    }
    const saved = z.object({ expiresAt: z.number(), draft: checkoutDraftSchema }).parse(stored);
    if (saved.expiresAt > now || saved.draft.attempt || saved.draft.walletIntentID || saved.draft.previousAttempt) return saved.draft;
  } catch {
    if (hasFinancialRecovery) throw new CheckoutRecoveryUnavailableError("We couldn't read your saved purchase. Keep this tab open and contact Butter before starting another payment.");
  }
  storage.removeItem(key);
  return null;
}

export function writeCheckoutDraft(storage: Storage, key: string, draft: CheckoutDraft, now = Date.now()): void {
  // Persist only unfinished input, never credentials, card data or default fields.
  const parsed = checkoutDraftSchema.parse(draft);
  const empty = emptyCheckoutDraft();
  const fields = Object.fromEntries(Object.entries(parsed).filter(([field, value]) =>
    JSON.stringify(value) !== JSON.stringify(empty[field as keyof CheckoutDraft])));
  if (!Object.keys(fields).length) { storage.removeItem(key); return; }
  const recovery: Record<string, unknown> = {};
  for (const field of ["attempt", "previousAttempt", "walletIntentID", "registrationID", "paymentPhase", "editingPayment"]) {
    if (field in fields) { recovery[field] = fields[field]; delete fields[field]; }
  }
  storage.setItem(key, JSON.stringify({ expiresAt: now + 24 * 60 * 60 * 1000, draft: fields, recovery }));
}

export function restoreCheckoutURL(search: string, draft: CheckoutDraft): URLSearchParams {
  const next = new URLSearchParams(search);
  next.set("step", draft.step);
  if (draft.ticketVariationID) next.set("ticketVariationID", draft.ticketVariationID);
  else next.delete("ticketVariationID");
  if (draft.discountCode) next.set("discountCode", draft.discountCode);
  else next.delete("discountCode");
  return next;
}

type OutcomeRegistration = {
  id?: string;
  originalQuantity?: number | null;
  reservations?: { id: string; state: string; isPurchaser?: boolean; formComplete?: boolean }[] | null;
  purchaseWalletIntentID?: string | null;
  purchaser?: { id: string } | null;
  status: { requestState?: string | null; approvalRequired?: boolean | null; submittedAt?: string | null; confirmedAt?: string | null; cancelledAt?: string | null };
};
type OutcomeIntent = {
  id: string;
  state: string;
  walletIntentEventTicketPurchase?: { eventID: string; eventRequestID?: string | null } | null;
  walletIntentEventTicketBatchPurchase?: {
    eventID: string;
    eventRegistrationID: string;
    quantity: number;
    items: { eventReservationID: string }[];
  } | null;
};
type OutcomeEvent = {
  myRegistration?: OutcomeRegistration | null;
  hasEnded: boolean;
  canceled: boolean;
  myTicketConfirmed: boolean;
  myAttendance?: {
    userID: string;
    cancelledAt?: string | null;
    refundRequestedAt?: string | null;
    eventTicketPurchaseWalletIntentID?: string | null;
    eventRegistrationID?: string | null;
    eventReservationID?: string | null;
    reservation?: { state: string; formComplete: boolean; registration?: OutcomeRegistration | null } | null;
  } | null;
  myRequest?: {
    id: string;
    state: string;
    submittedAt?: string | null;
    eventRegistrationID?: string | null;
    reservation?: { registration?: OutcomeRegistration | null } | null;
  } | null;
};

export function checkoutOutcome(eventId: string, viewerID: string, intent: OutcomeIntent | null | undefined, event: OutcomeEvent | null | undefined) {
  const legacy = intent?.walletIntentEventTicketPurchase;
  const batch = intent?.walletIntentEventTicketBatchPurchase;
  if (!intent || !event || legacy?.eventID !== eventId && batch?.eventID !== eventId) return "missing";
  const group = event.myRegistration;
  if (batch && group && (group.originalQuantity ?? 1) > 1) {
    if (group.id !== batch.eventRegistrationID || group.purchaseWalletIntentID !== intent.id || group.purchaser?.id !== viewerID) return "missing";
    if (event.canceled || group.status.cancelledAt || ["DECLINED", "EXPIRED"].includes(group.status.requestState ?? "")) return "failed";
    const active = group.reservations?.filter(value => value.state !== "CANCELLED") ?? [];
    if (!active.length || intent.state === "CANCELLED" || intent.state === "FAILED") return "failed";
    if (intent.state === "PROCESSED" && active.every(value => value.state === "CONFIRMED")) return "confirmed";
    if (group.status.approvalRequired === true && group.status.submittedAt && group.status.requestState === "PENDING" && active.every(value => value.state === "PENDING" || value.state === "RESERVED")) return "request";
    return "processing";
  }
  if (batch && (batch.quantity !== 1 || batch.items.length !== 1)) return "missing";

  const attendee = event.myAttendance;
  const registration = attendee?.reservation?.registration;
  const ownsActiveAttendance = attendee?.userID === viewerID && !attendee.cancelledAt && !attendee.refundRequestedAt;
  const settled = intent.state === "PROCESSED" && ownsActiveAttendance;
  if (settled && legacy && (event.myTicketConfirmed || event.hasEnded && !event.canceled) &&
    attendee.eventTicketPurchaseWalletIntentID === intent.id &&
    (!registration || registration.purchaseWalletIntentID === intent.id && !registration.status.cancelledAt && attendee.reservation?.state === "CONFIRMED")) return "confirmed";
  if (settled && batch && (event.myTicketConfirmed || event.hasEnded && !event.canceled) &&
    registration?.purchaser?.id === viewerID && !registration.status.cancelledAt &&
    registration.purchaseWalletIntentID === intent.id &&
    attendee.reservation?.state === "CONFIRMED" && registration.status.confirmedAt &&
    attendee.eventRegistrationID === batch.eventRegistrationID &&
    attendee.eventReservationID === batch.items[0].eventReservationID) return "confirmed";

  const request = event.myRequest;
  if (request?.submittedAt && request.state !== "APPROVING" && request.state !== "IN_ACCEPTED_WAITLIST") {
    if (legacy?.eventRequestID === request.id) return "request";
    if (batch && request.eventRegistrationID === batch.eventRegistrationID &&
      request.reservation?.registration?.purchaseWalletIntentID === intent.id &&
      request.reservation.registration.purchaser?.id === viewerID &&
      request.reservation.registration.status.approvalRequired === true &&
      request.reservation.registration.status.submittedAt) return "request";
  }
  if (intent.state === "CANCELLED" || intent.state === "FAILED") return "failed";
  return "processing";
}

export function anonymousCheckoutTransfer(draft: CheckoutDraft): CheckoutDraft {
  // Only the cart and phone challenge cross the sign-in boundary, never another profile or payment.
  return { ...emptyCheckoutDraft(), ticketVariationID: draft.ticketVariationID, discountCode: draft.discountCode,
    groupSlots: draft.groupSlots, purchaserSlotID: draft.purchaserSlotID,
    phone: draft.phone, phoneInput: draft.phoneInput, phoneCountry: draft.phoneCountry, verificationSentAt: draft.verificationSentAt, step: draft.step };
}

// A rejected replacement has not superseded the accepted unpaid attempt.
export function rejectedCheckoutAttempt(draft: CheckoutDraft): Partial<CheckoutDraft> {
  const previous = draft.previousAttempt;
  return {
    attempt: previous?.command ?? null,
    walletIntentID: previous?.walletIntentID ?? draft.walletIntentID,
    registrationID: previous?.registrationID ?? draft.registrationID,
    previousAttempt: null,
    editingPayment: true,
  };
}
